import { useMemo } from 'react'
import type { ShopI18nContextValue } from '../../../../i18n/provider/types'
import { isPasswordStrongEnough } from '../../../helpers/passwordStrength'
import type { CustomerAuthField } from '../../../hooks/useCustomerAuthForm'
import type { ResetPasswordFlowState } from './ResetPasswordFlow.types'

type Translate = ShopI18nContextValue['t']

type UseResetPasswordViewModelInput = {
  state: ResetPasswordFlowState
  t: Translate
}

type UseResetPasswordViewModelOutput = {
  fieldErrors: Partial<Record<CustomerAuthField, string>>
  canSubmit: boolean
  title: string
  subtitle: string
}

export const useResetPasswordViewModel = ({
  state,
  t,
}: UseResetPasswordViewModelInput): UseResetPasswordViewModelOutput => {
  const passwordStrong = isPasswordStrongEnough(state.password)
  const passwordsMatch = state.confirmPassword === state.password

  const passwordError = useMemo(() => {
    const invalidMessage = !state.password.trim()
      ? t('customer.auth.validation.requiredField')
      : !passwordStrong
        ? t('customer.auth.validation.passwordWeak')
        : ''
    const errorVisible =
      (state.passwordTouched && state.passwordDirty && Boolean(invalidMessage)) ||
      (state.formSubmitted && Boolean(invalidMessage))
    return errorVisible ? invalidMessage : ''
  }, [passwordStrong, state.formSubmitted, state.password, state.passwordDirty, state.passwordTouched, t])

  const confirmPasswordError = useMemo(() => {
    const invalidMessage = !state.confirmPassword.trim()
      ? t('customer.auth.validation.requiredField')
      : !passwordsMatch
        ? t('customer.auth.validation.passwordMismatch')
        : ''
    const errorVisible =
      (state.confirmPasswordTouched && state.confirmPasswordDirty && Boolean(invalidMessage)) ||
      (state.formSubmitted && Boolean(invalidMessage))
    return errorVisible ? invalidMessage : ''
  }, [
    passwordsMatch,
    state.confirmPassword,
    state.confirmPasswordDirty,
    state.confirmPasswordTouched,
    state.formSubmitted,
    t,
  ])

  const fieldErrors = useMemo<Partial<Record<CustomerAuthField, string>>>(
    () => ({
      password: passwordError,
      confirmPassword: confirmPasswordError,
    }),
    [confirmPasswordError, passwordError],
  )

  const canSubmit =
    Boolean(state.token) &&
    Boolean(state.password.trim()) &&
    passwordStrong &&
    Boolean(state.confirmPassword.trim()) &&
    passwordsMatch

  const title = state.success
    ? t('customer.auth.resetPassword.successTitle')
    : state.pageState === 'expired'
      ? t('customer.auth.resetPassword.expiredTitle')
      : state.pageState === 'invalid'
        ? t('customer.auth.resetPassword.invalidTitle')
        : t('customer.auth.resetPassword.title')

  const subtitle = state.success
    ? t('customer.auth.resetPassword.successBody')
    : state.pageState === 'expired'
      ? t('customer.auth.resetPassword.expiredDescription')
      : state.pageState === 'invalid'
        ? t('customer.auth.resetPassword.invalidDescription')
        : t('customer.auth.resetPassword.subtitle')

  return {
    fieldErrors,
    canSubmit,
    title,
    subtitle,
  }
}
